// Font library for /apps/pdf-tools/pdf-editor.
//
// Open-licence faces vendored under /fonts (SIL OFL / Apache / the
// Bitstream Vera licence), fetched lazily the first time a family is
// used and embedded into the PDF by the worker (same code path as the
// document-font re-embed). Encoding stays WinAnsi — every family here
// covers it. Uploaded fonts live in memory for the session only.

export type FontVariant = "regular" | "bold" | "italic" | "boldItalic";

export interface LibraryFont {
  /** Stable id — the format bar stores `lib:<id>` as the family. */
  id: string;
  label: string;
  category: "sans" | "serif" | "mono" | "display";
  /** CSS stack that approximates the face while its file loads. */
  cssFallback: string;
  files: Partial<Record<FontVariant, string>>;
}

export const FONT_LIBRARY: LibraryFont[] = [
  {
    id: "opensans",
    label: "Open Sans",
    category: "sans",
    cssFallback: "'Segoe UI', Arial, sans-serif",
    files: {
      regular: "/fonts/OpenSans-Regular.ttf",
      bold: "/fonts/OpenSans-Bold.ttf",
      italic: "/fonts/OpenSans-Italic.ttf",
      boldItalic: "/fonts/OpenSans-BoldItalic.ttf",
    },
  },
  {
    id: "libsans",
    label: "Liberation Sans",
    category: "sans",
    cssFallback: "Arial, Helvetica, sans-serif",
    files: {
      regular: "/fonts/LiberationSans-Regular.ttf",
      bold: "/fonts/LiberationSans-Bold.ttf",
      italic: "/fonts/LiberationSans-Italic.ttf",
      boldItalic: "/fonts/LiberationSans-BoldItalic.ttf",
    },
  },
  {
    id: "dejavu",
    label: "DejaVu Sans",
    category: "sans",
    cssFallback: "Verdana, sans-serif",
    files: {
      regular: "/fonts/DejaVuSans-Regular.ttf",
      bold: "/fonts/DejaVuSans-Bold.ttf",
    },
  },
  {
    id: "libserif",
    label: "Liberation Serif",
    category: "serif",
    cssFallback: "'Times New Roman', Times, serif",
    files: {
      regular: "/fonts/LiberationSerif-Regular.ttf",
      bold: "/fonts/LiberationSerif-Bold.ttf",
      italic: "/fonts/LiberationSerif-Italic.ttf",
      boldItalic: "/fonts/LiberationSerif-BoldItalic.ttf",
    },
  },
  {
    id: "montserrat",
    label: "Montserrat",
    category: "display",
    cssFallback: "'Trebuchet MS', Futura, sans-serif",
    files: {
      regular: "/fonts/Montserrat-Regular.otf",
      bold: "/fonts/Montserrat-Bold.otf",
      italic: "/fonts/Montserrat-Italic.otf",
      boldItalic: "/fonts/Montserrat-BoldItalic.otf",
    },
  },
  {
    id: "libmono",
    label: "Liberation Mono",
    category: "mono",
    cssFallback: "'Courier New', monospace",
    files: {
      regular: "/fonts/LiberationMono-Regular.ttf",
      bold: "/fonts/LiberationMono-Bold.ttf",
    },
  },
];

/** An uploaded (session-only) face. */
export interface CustomFont {
  id: string;
  label: string;
  bytes: ArrayBuffer;
}

export const isLibraryFamily = (family: string): boolean => family.startsWith("lib:");
export const isCustomFamily = (family: string): boolean => family.startsWith("upload:");
export const libraryEntry = (family: string): LibraryFont | null =>
  FONT_LIBRARY.find((f) => `lib:${f.id}` === family) ?? null;

/** Best file for the requested weight/slant, falling back towards
 *  regular when the family lacks a variant. */
export function pickVariant(
  f: LibraryFont,
  bold: boolean,
  italic: boolean
): { variant: FontVariant; url: string } {
  const order: FontVariant[] =
    bold && italic
      ? ["boldItalic", "bold", "italic", "regular"]
      : bold
        ? ["bold", "regular"]
        : italic
          ? ["italic", "regular"]
          : ["regular"];
  for (const v of order) {
    const url = f.files[v];
    if (url) return { variant: v, url };
  }
  const [variant, url] = Object.entries(f.files)[0]! as [FontVariant, string];
  return { variant, url };
}

const bytesCache = new Map<string, Promise<ArrayBuffer>>();

/** Fetch (once) a library font file. */
export function fetchFontBytes(url: string): Promise<ArrayBuffer> {
  let p = bytesCache.get(url);
  if (!p) {
    p = fetch(url).then((r) => {
      if (!r.ok) throw new Error(`font download failed (${r.status})`);
      return r.arrayBuffer();
    });
    p.catch(() => bytesCache.delete(url)); // allow retry after a failure
    bytesCache.set(url, p);
  }
  return p;
}

const faceCache = new Map<string, Promise<string>>();

/** Register a FontFace for WYSIWYG editing; resolves to the CSS
 *  family name to use. */
export function ensureFontFace(key: string, source: string | ArrayBuffer): Promise<string> {
  let p = faceCache.get(key);
  if (!p) {
    const cssName = `pe-lib-${key.replace(/[^a-z0-9-]/gi, "")}`;
    p = (async () => {
      const buf = typeof source === "string" ? await fetchFontBytes(source) : source;
      const face = new FontFace(cssName, buf.slice(0));
      await face.load();
      document.fonts.add(face);
      return cssName;
    })();
    p.catch(() => faceCache.delete(key));
    faceCache.set(key, p);
  }
  return p;
}
